"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation, hasDropFooter } from "@/components/waves/drops/drop.types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, type ReactNode } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly footer?: ReactNode;
  readonly showInteractions?: boolean | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = typeof drop.rank === "number" && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  }
  if (rank === 1) {
    return `${baseClasses} ${getRankHoverBorderClass(1)}`;
  }
  if (rank === 2) {
    return `${baseClasses} ${getRankHoverBorderClass(2)}`;
  }
  if (rank === 3) {
    return `${baseClasses} ${getRankHoverBorderClass(3)}`;
  }
  return `${baseClasses} tw-border-iron-800`;
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(79);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    footer,
    showInteractions: t1,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVotingModalOpen,
    open: openVotingModal,
    close: closeVotingModal
  } = useVotingModalState(isVotingActionLocked);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t4;
  if ($[0] !== drop.parts) {
    t4 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const artworkMedia = t4;
  let t5;
  if ($[2] !== drop || $[3] !== isActiveDrop) {
    t5 = getBorderClasses(drop, isActiveDrop);
    $[2] = drop;
    $[3] = isActiveDrop;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const borderClasses = t5;
  let t6;
  if ($[5] !== drop || $[6] !== onReply) {
    t6 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[5] = drop;
    $[6] = onReply;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleOnReply = t6;
  let t7;
  if ($[8] !== drop) {
    t7 = <MemeDropArtistInfo drop={drop} />;
    $[8] = drop;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== title) {
    t8 = <MemeDropHeader title={title} />;
    $[10] = title;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== description) {
    t9 = <MemeDropDescription description={description} />;
    $[12] = description;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5">{t8}{t9}</div>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7) {
    t11 = <div className="tw-p-4">{t7}{t10}</div>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== artworkMedia || $[21] !== location) {
    t12 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[20] = artworkMedia;
    $[21] = location;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== drop) {
    t13 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[23] = drop;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const content = t14;
  const t15 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t16 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t17;
  if ($[29] !== content || $[30] !== drop || $[31] !== handleOnReply || $[32] !== showInteractions || $[33] !== showReplyAndQuote) {
    t17 = showInteractions ? <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{content}</DropMobileMenuHandler> : content;
    $[29] = content;
    $[30] = drop;
    $[31] = handleOnReply;
    $[32] = showInteractions;
    $[33] = showReplyAndQuote;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== drop.context_profile_context || $[36] !== drop.raters_count || $[37] !== drop.rating || $[38] !== drop.rating_prediction || $[39] !== drop.top_raters || $[40] !== drop.wave.voting_credit_type) {
    t18 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats current={drop.rating} projected={drop.rating_prediction} votingCreditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={drop.top_raters} userContext={drop.context_profile_context} /></div>;
    $[35] = drop.context_profile_context;
    $[36] = drop.raters_count;
    $[37] = drop.rating;
    $[38] = drop.rating_prediction;
    $[39] = drop.top_raters;
    $[40] = drop.wave.voting_credit_type;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== canShowVote || $[43] !== drop || $[44] !== isVotingActionLocked || $[45] !== openVotingModal || $[46] !== showInteractions) {
    t19 = canShowVote && showInteractions && !isVotingActionLocked && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp3}><VotingModalButton drop={drop} onClick={openVotingModal} /></div></div>;
    $[42] = canShowVote;
    $[43] = drop;
    $[44] = isVotingActionLocked;
    $[45] = openVotingModal;
    $[46] = showInteractions;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t18 || $[49] !== t19) {
    t20 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t18}{t19}</div>;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== drop || $[52] !== showInteractions) {
    t21 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[51] = drop;
    $[52] = showInteractions;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== footer) {
    t22 = hasDropFooter(footer) && <div className="tw-px-4 tw-pb-4 tw-pt-2">{footer}</div>;
    $[54] = footer;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== drop || $[57] !== handleOnReply || $[58] !== isMobile || $[59] !== showInteractions || $[60] !== showReplyAndQuote) {
    t23 = showInteractions && <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[56] = drop;
    $[57] = handleOnReply;
    $[58] = isMobile;
    $[59] = showInteractions;
    $[60] = showReplyAndQuote;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t16 || $[63] !== t17 || $[64] !== t20 || $[65] !== t21 || $[66] !== t22 || $[67] !== t23) {
    t24 = <div className={t16}>{t17}{t20}{t21}{t22}{t23}</div>;
    $[62] = t16;
    $[63] = t17;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== closeVotingModal || $[70] !== drop || $[71] !== isMobileScreen || $[72] !== isVotingModalOpen || $[73] !== showInteractions) {
    t25 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={closeVotingModal} />);
    $[69] = closeVotingModal;
    $[70] = drop;
    $[71] = isMobileScreen;
    $[72] = isVotingModalOpen;
    $[73] = showInteractions;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== t15 || $[76] !== t24 || $[77] !== t25) {
    t26 = <div className="tw-w-full tw-@container"><div className={t15}>{t24}{t25}</div></div>;
    $[75] = t15;
    $[76] = t24;
    $[77] = t25;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  return t26;
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}